import type { YinshState } from "@cloudnest/yinsh-engine";
import {
  applyForfeit,
  applyMoveRing,
  applyPlaceMarker,
  applyPlaceRing,
  applyRemoveRing,
  applySelectRow,
  getValidRingDestinations,
  initGame,
  validateForfeit,
  validateMoveRing,
  validatePlaceMarker,
  validatePlaceRing,
  validateRemoveRing,
  validateSelectRow,
} from "@cloudnest/yinsh-engine";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  createReplayShare as createReplayShareRequest,
  type GameApi,
  type GameApiMode,
  type GameEvent,
  type ReplayShare,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";
import type { YinshReplayEventType } from "./event-adapter";
import {
  buildYinshReplaySnapshots,
  type YinshReplaySnapshot,
} from "./replay-runner";
import { resolveYinshGameApiMode } from "./route-mode";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApi(): GameApi {
  const nextMode = resolveYinshGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("yinsh", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

function isGamePlatformMode(): boolean {
  return resolveYinshGameApiMode() === "game-platform";
}

interface MultiplayerGameState {
  readonly gameState: YinshState | null;
  readonly roomId: string | null;
  readonly myParticipantId: string | null;
  readonly isHost: boolean;
  readonly lastAppliedSeq: number;
  readonly lastError: string | null;
  readonly selectedRingPosition: string | null;
  readonly validDestinations: readonly string[];
}

const initialState: MultiplayerGameState = {
  gameState: null,
  roomId: null,
  myParticipantId: null,
  isHost: false,
  lastAppliedSeq: 0,
  lastError: null,
  selectedRingPosition: null,
  validDestinations: [],
};

let state: MultiplayerGameState = { ...initialState };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function applyEvent(
  gameState: YinshState,
  eventType: string,
  payload: Record<string, unknown>,
): YinshState {
  switch (eventType) {
    case "init":
      return payload.state as unknown as YinshState;

    case "place_ring":
      return applyPlaceRing(gameState, {
        type: "PLACE_RING",
        player: payload.player as "WHITE" | "BLACK",
        position: payload.position as string,
      });

    case "place_marker":
      return applyPlaceMarker(gameState, {
        type: "PLACE_MARKER",
        player: payload.player as "WHITE" | "BLACK",
        ringPosition: payload.ringPosition as string,
      });

    case "move_ring":
      return applyMoveRing(gameState, {
        type: "MOVE_RING",
        player: payload.player as "WHITE" | "BLACK",
        from: payload.from as string,
        to: payload.to as string,
      });

    case "take_turn": {
      const afterPlace = applyPlaceMarker(gameState, {
        type: "PLACE_MARKER",
        player: payload.player as "WHITE" | "BLACK",
        ringPosition: payload.ringPosition as string,
      });
      return applyMoveRing(afterPlace, {
        type: "MOVE_RING",
        player: payload.player as "WHITE" | "BLACK",
        from: payload.ringPosition as string,
        to: payload.destination as string,
      });
    }

    case "select_row":
      return applySelectRow(gameState, {
        type: "SELECT_ROW",
        player: payload.player as "WHITE" | "BLACK",
        positions: payload.positions as [
          string,
          string,
          string,
          string,
          string,
        ],
      });

    case "remove_ring":
      return applyRemoveRing(gameState, {
        type: "REMOVE_RING",
        player: payload.player as "WHITE" | "BLACK",
        position: payload.position as string,
      });

    case "forfeit":
      return applyForfeit(gameState, {
        type: "FORFEIT",
        player: payload.player as "WHITE" | "BLACK",
      });

    default:
      console.warn(`Unknown YINSH event type: ${eventType}`);
      return gameState;
  }
}

function replayYinshEvents(events: readonly GameEvent[]): {
  readonly gameState: YinshState | null;
  readonly lastSeq: number;
} {
  let gameState: YinshState | null = null;
  let lastSeq = 0;

  for (const event of events) {
    const payload = event.payload as Record<string, unknown>;
    if (event.eventType === "init") {
      gameState = payload.state as unknown as YinshState;
    } else if (gameState) {
      gameState = applyEvent(gameState, event.eventType, payload);
    }
    lastSeq = event.sequenceNumber;
  }

  return { gameState, lastSeq };
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function replayEventsUntilSequence(
  roomId: string,
  minSequenceNumber: number,
): Promise<boolean> {
  for (let attempt = 0; attempt < 8; attempt++) {
    const response = await getGameApi().getGameEvents(roomId);
    const events = response.success ? (response.data ?? []) : [];
    const latestSequence = events.reduce(
      (latest, event) => Math.max(latest, event.sequenceNumber),
      0,
    );

    if (latestSequence >= minSequenceNumber) {
      const replay = replayYinshEvents(events);
      state = {
        ...state,
        gameState: replay.gameState,
        lastAppliedSeq: replay.lastSeq,
        selectedRingPosition: null,
        validDestinations: [],
        lastError: null,
      };
      notify();
      return true;
    }

    await wait(250);
  }

  return false;
}

export const multiplayerGameActions = {
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    playerNames: readonly string[] = [],
  ): Promise<void> => {
    const myId = getCurrentUserId() ?? "";

    state = { ...initialState, roomId, myParticipantId: myId, isHost: true };
    notify();

    // Fetch existing events before doing anything. If events exist the host is
    // reconnecting (page reload / remount) — replay from the log instead of
    // submitting a new "init" that would overwrite in-progress game state.
    const eventsResponse = await getGameApi().getGameEvents(roomId);
    const existingEvents = eventsResponse.success
      ? (eventsResponse.data ?? [])
      : [];

    if (existingEvents.length > 0) {
      const replay = replayYinshEvents(existingEvents);
      state = {
        ...state,
        gameState: replay.gameState,
        lastAppliedSeq: replay.lastSeq,
      };
      notify();
      return;
    }

    // No events yet — brand-new game: create initial state and persist it.
    // Prefer the readable display names provided by the caller; only fall
    // back to user IDs if names are missing (legacy / no-resolver path).
    const [p1Id, p2Id] = playerIds;
    const [p1Name, p2Name] = playerNames;
    const gameState = initGame({
      player1Name: p1Name?.trim() || p1Id || "Player 1",
      player2Name: p2Name?.trim() || p2Id || "Player 2",
    });
    state = { ...state, gameState };
    notify();
    try {
      const response = await getGameApi().submitEvent(
        roomId,
        "init",
        isGamePlatformMode()
          ? {
              playerNames: [
                gameState.players[0].name,
                gameState.players[1].name,
              ],
            }
          : { state: gameState as unknown as Record<string, unknown> },
      );
      if (response.success && response.data) {
        if (isGamePlatformMode()) {
          const synced = await replayEventsUntilSequence(
            roomId,
            response.data.sequenceNumber,
          );
          if (!synced) {
            state = {
              ...state,
              lastError: "서버 초기 상태 동기화가 지연되고 있습니다",
            };
            notify();
          }
          return;
        }
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error ? error.message : "게임 초기화에 실패했습니다",
      };
      notify();
    }
  },

  initializeAsGuest: async (roomId: string): Promise<void> => {
    const myId = getCurrentUserId() ?? "";
    state = {
      ...initialState,
      roomId,
      myParticipantId: myId,
      isHost: false,
    };
    notify();

    // Replay any events the host has already produced. This is the path
    // taken when a guest rejoins an IN_PROGRESS game from the lobby — no
    // new "init" event will be pushed via WS, so we need to pull state
    // from the event log just like the host's reconnect path.
    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data || response.data.length === 0) {
        return;
      }

      const replay = replayYinshEvents(response.data);
      if (replay.gameState && state.roomId === roomId) {
        state = {
          ...state,
          gameState: replay.gameState,
          lastAppliedSeq: replay.lastSeq,
        };
        notify();
      }
    } catch {
      // Non-fatal — the WS will eventually push events and onReconnect
      // (after the first reconnect) will trigger a full replay.
    }
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    state = {
      ...initialState,
      roomId,
      myParticipantId: null,
      isHost: false,
    };
    notify();

    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data || response.data.length === 0) {
        return;
      }

      const replay = replayYinshEvents(response.data);
      if (replay.gameState && state.roomId === roomId) {
        state = {
          ...state,
          gameState: replay.gameState,
          lastAppliedSeq: replay.lastSeq,
        };
        notify();
      }
    } catch {
      // Non-fatal — observer event replay will run again after reconnect.
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const response = await getGameApi().getGameEvents(roomId);
      if (!response.success || !response.data) return;

      const replay = replayYinshEvents(response.data);
      state = {
        ...state,
        gameState: replay.gameState,
        lastAppliedSeq: replay.lastSeq,
      };
      notify();
    } catch (error) {
      state = {
        ...state,
        lastError:
          error instanceof Error
            ? error.message
            : "이벤트를 불러올 수 없습니다",
      };
      notify();
    }
  },

  /**
   * Build read-only move-by-move snapshots for the just-finished game by
   * fetching the persisted relay event log and replaying it through the same
   * runner the dev replay route uses. The persisted events are already in the
   * runner's expected shape (`init` is normalized to `{ state }` server-side),
   * so no adaptation beyond narrowing the event-type string is needed.
   */
  fetchReplaySnapshots: async (
    roomId: string,
  ): Promise<readonly YinshReplaySnapshot[]> => {
    const response = await getGameApi().getGameEvents(roomId);
    if (!response.success || !response.data) {
      throw new Error(response.error ?? "이벤트를 불러올 수 없습니다");
    }
    const events = response.data.map((event) => ({
      sequenceNumber: event.sequenceNumber,
      eventType: event.eventType as YinshReplayEventType,
      userId: event.userId,
      payload: event.payload,
    }));
    return buildYinshReplaySnapshots(events);
  },

  /**
   * Snapshot the finished game's event log into a standalone, shareable replay
   * (new isolated data — the live game is never touched). Returns the share
   * with its public code.
   */
  createReplayShare: async (
    events: readonly unknown[],
  ): Promise<ReplayShare> => {
    const response = await createReplayShareRequest({
      gameType: "yinsh",
      events,
    });
    if (!response.success || !response.data) {
      throw new Error(response.error ?? "공유 링크 생성에 실패했습니다");
    }
    return response.data;
  },

  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    if (!state.gameState && eventType !== "init") return;

    // Deduplicate the WS echo of our own optimistic ring placement.
    // placeRing applies the ring locally before the HTTP response returns, so
    // the WS echo can arrive with seq > lastAppliedSeq while the ring is
    // already in the board. Re-applying would double-count ringsOnBoard,
    // eventually failing validatePlaceRing with "All your rings are already placed".
    if (eventType === "place_ring" && state.gameState) {
      const pos = payload.position as string;
      const cell = state.gameState.board[pos];
      if (cell?.type === "RING" && cell.player === (payload.player as string)) {
        state = {
          ...state,
          lastAppliedSeq: seq,
          selectedRingPosition: null,
          validDestinations: [],
        };
        notify();
        return;
      }
    }

    const nextState = state.gameState
      ? applyEvent(state.gameState, eventType, payload)
      : eventType === "init"
        ? (payload.state as unknown as YinshState)
        : null;

    state = {
      ...state,
      gameState: nextState,
      lastAppliedSeq: seq,
      selectedRingPosition: null,
      validDestinations: [],
    };
    notify();
  },

  selectRing: (position: string | null): void => {
    if (!state.gameState || position === null) {
      state = { ...state, selectedRingPosition: null, validDestinations: [] };
      notify();
      return;
    }
    const dests = getValidRingDestinations(state.gameState.board, position);
    state = {
      ...state,
      selectedRingPosition: position,
      validDestinations: dests,
    };
    notify();
  },

  placeRing: async (
    position: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!state.gameState || !state.roomId)
      return { success: false, error: "No game" };
    const roomId = state.roomId;
    const action = {
      type: "PLACE_RING" as const,
      player: state.gameState.currentPlayer,
      position,
    };
    const v = validatePlaceRing(state.gameState, action);
    if (!v.valid) return { success: false, error: v.error };
    // Optimistic update — ring appears immediately; WS echo is deduped via lastAppliedSeq
    const prevGameState = state.gameState;
    state = { ...state, gameState: applyPlaceRing(state.gameState, action) };
    notify();
    try {
      const response = await getGameApi().submitEvent(roomId, "place_ring", {
        player: action.player,
        position,
      });
      if (response.success && response.data) {
        state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
        notify();
      }
    } catch (error) {
      state = { ...state, gameState: prevGameState };
      notify();
      return {
        success: false,
        error: error instanceof Error ? error.message : "제출 실패",
      };
    }
    return { success: true };
  },

  takeTurn: async (
    ringPosition: string,
    destination: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!state.gameState || !state.roomId)
      return { success: false, error: "No game" };
    const player = state.gameState.currentPlayer;

    // Validate both steps locally before submitting the single atomic event
    const placeAction = {
      type: "PLACE_MARKER" as const,
      player,
      ringPosition,
    };
    const vp = validatePlaceMarker(state.gameState, placeAction);
    if (!vp.valid) return { success: false, error: vp.error };

    const afterPlace = applyPlaceMarker(state.gameState, placeAction);
    const moveAction = {
      type: "MOVE_RING" as const,
      player,
      from: ringPosition,
      to: destination,
    };
    const vm = validateMoveRing(afterPlace, moveAction);
    if (!vm.valid) return { success: false, error: vm.error };

    try {
      await getGameApi().submitEvent(state.roomId, "take_turn", {
        player,
        ringPosition,
        destination,
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "제출 실패",
      };
    }
    state = { ...state, selectedRingPosition: null, validDestinations: [] };
    notify();
    return { success: true };
  },

  selectRow: async (
    positions: readonly [string, string, string, string, string],
  ): Promise<{ success: boolean; error?: string }> => {
    if (!state.gameState || !state.roomId)
      return { success: false, error: "No game" };
    const action = {
      type: "SELECT_ROW" as const,
      player: state.gameState.currentPlayer,
      positions,
    };
    const v = validateSelectRow(state.gameState, action);
    if (!v.valid) return { success: false, error: v.error };
    try {
      await getGameApi().submitEvent(state.roomId, "select_row", {
        player: action.player,
        positions,
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "제출 실패",
      };
    }
    return { success: true };
  },

  removeRing: async (
    position: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!state.gameState || !state.roomId)
      return { success: false, error: "No game" };
    const action = {
      type: "REMOVE_RING" as const,
      player: state.gameState.currentPlayer,
      position,
    };
    const v = validateRemoveRing(state.gameState, action);
    if (!v.valid) return { success: false, error: v.error };
    try {
      await getGameApi().submitEvent(state.roomId, "remove_ring", {
        player: action.player,
        position,
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "제출 실패",
      };
    }
    return { success: true };
  },

  forfeit: async (): Promise<{ success: boolean; error?: string }> => {
    if (!state.gameState || !state.roomId) {
      return { success: false, error: "No game" };
    }
    const roomId = state.roomId;
    const player: "WHITE" | "BLACK" = state.isHost ? "WHITE" : "BLACK";
    const action = { type: "FORFEIT" as const, player };
    const v = validateForfeit(state.gameState, action);
    if (!v.valid) return { success: false, error: v.error };

    try {
      await getGameApi().submitEvent(roomId, "forfeit", { player });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "기권 실패",
      };
    }
    // Optimistically end the game locally so the giver sees the result
    // immediately, even before the WS echo arrives.
    state = {
      ...state,
      gameState: applyForfeit(state.gameState, action),
      selectedRingPosition: null,
      validDestinations: [],
    };
    notify();
    return { success: true };
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = { ...initialState };
    gameApi = null;
    gameApiMode = null;
    notify();
  },
};

export const multiplayerGameStore = {
  getState: (): MultiplayerGameState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useMultiplayerGameStore(): MultiplayerGameState {
  return useSyncExternalStore(
    multiplayerGameStore.subscribe,
    multiplayerGameStore.getState,
  );
}

export function useMultiplayerGameActions(): typeof multiplayerGameActions {
  return multiplayerGameActions;
}
